import { useState, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, Eye, EyeOff, Globe, CheckCircle2, Clock, FileText,
  Download, Calendar, ChevronRight, AlertCircle, Info, Send,
  Image, ArrowRight, Sparkles, Edit, Lock, Truck, Shield, Target
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function ClientPortal() {
  const { clientPortalData, workspace, projects, conditions, blockers, trades, gates, evidence, tradeAttendance } = useDemoState();
  const [published, setPublished] = useState(false);
  const [hideInternal, setHideInternal] = useState(true);
  const [editDraft, setEditDraft] = useState(false);
  const data = clientPortalData;

  const proj = projects.find(p => p.id === 'proj-001');
  const projConditions = conditions.filter(c => c.projectId === 'proj-001');
  const projBlockers = blockers.filter(b => b.projectId === 'proj-001');
  const gate = gates.find(g => g.id === 'gate-002');
  const mainTrade = trades.find(t => t.id === 'trade-001');

  const liveStats = useMemo(() => {
    const cleared = projConditions.filter(c => c.status === 'cleared').length;
    const total = projConditions.length;
    const overallProgress = total > 0 ? Math.round((cleared / total) * 100) : 0;
    return { cleared, total, overallProgress, blockerCount: projBlockers.length };
  }, [projConditions, projBlockers]);

  const aiWeeklyDraft = useMemo(() => {
    const cleared = liveStats.cleared;
    const remaining = liveStats.total - cleared;
    const blockerCount = liveStats.blockerCount;
    if (remaining === 0) {
      return `Great news! All ${liveStats.total} conditions for ${proj?.name || '12 Smith St'} have been cleared. The project is on track and the team is preparing for the next milestone. We expect slab works to begin shortly.`;
    }
    return `Progress update for ${proj?.name || '12 Smith St'}: ${cleared} of ${liveStats.total} approval conditions have been cleared so far. ${remaining} item${remaining > 1 ? 's' : ''} remain${remaining === 1 ? 's' : ''} in progress${blockerCount > 0 ? `, with ${blockerCount} item${blockerCount > 1 ? 's' : ''} requiring follow-up from the project team` : ''}. The team is actively working to finalise the remaining engineering sign-offs needed before we move into the slab stage.`;
  }, [liveStats, proj]);

  const handlePublish = () => {
    setPublished(true);
    toast.success('Update published', { description: 'Client portal updated for ' + (proj?.name || 'project') });
  };

  const stageStatus = useMemo(() => {
    const stageGate = gate;
    if (!stageGate) return 'unknown';
    return stageGate.state === 'cleared' ? 'ready' : 'not-ready';
  }, [gate]);

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="client-portal-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Creates a curated milestone update for your client. Internal workflow stays private. Only approved content is published.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Review the AI-generated update. Edit if needed. Publish when ready. The client sees only what you approve.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, office admin. The client sees the published result only.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live state summary - owner-scope status */}
        <Card className="border-slate-200 overflow-hidden" data-testid="owner-scope-status">
          <div className="bg-slate-900 px-5 py-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Live Project Status — Owner Scope</span>
            </div>
          </div>
          <CardContent className="py-4 px-5">
            <div className="grid grid-cols-5 gap-3">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Overall</p>
                <p className={`text-lg font-bold ${liveStats.overallProgress === 100 ? 'text-emerald-600' : 'text-blue-600'}`}>{liveStats.overallProgress}%</p>
                <p className="text-[10px] text-slate-500">{liveStats.cleared}/{liveStats.total} cleared</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Stage Gate</p>
                <p className={`text-lg font-bold ${stageStatus === 'ready' ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {stageStatus === 'ready' ? 'Ready' : 'Pending'}
                </p>
                <p className="text-[10px] text-slate-500">Pre-Slab</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Blockers</p>
                <p className={`text-lg font-bold ${liveStats.blockerCount === 0 ? 'text-emerald-600' : 'text-red-600'}`}>{liveStats.blockerCount}</p>
                <p className="text-[10px] text-slate-500">active items</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Trade</p>
                <p className={`text-lg font-bold ${mainTrade?.status === 'released' ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {mainTrade?.status === 'released' ? 'Released' : mainTrade?.status === 'ready' ? 'Ready' : 'On Hold'}
                </p>
                <p className="text-[10px] text-slate-500">Concreter</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Evidence</p>
                <p className="text-lg font-bold text-blue-600">{evidence.filter(e => e.projectId === 'proj-001' && e.status === 'approved').length}</p>
                <p className="text-[10px] text-slate-500">approved files</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trades On-Site Today — Client-safe view */}
        {(() => {
          const onSiteTrades = trades.filter(t => t.status === 'checked-in' || t.status === 'cannot-start');
          if (onSiteTrades.length === 0) return null;
          return (
            <Card className="border-slate-200 overflow-hidden" data-testid="client-trades-on-site">
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Trades Working Today</span>
                </div>
                <span className="text-xs text-teal-100">{onSiteTrades.reduce((s, t) => s + (t.crewCount || 0), 0)} crew on site</span>
              </div>
              <CardContent className="py-3 px-5">
                <div className="space-y-2">
                  {onSiteTrades.map(t => (
                    <div key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border ${t.status === 'cannot-start' ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-100'}`} data-testid={`client-trade-${t.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.status === 'cannot-start' ? 'bg-amber-100' : 'bg-teal-100'}`}>
                        <Truck className={`w-4 h-4 ${t.status === 'cannot-start' ? 'text-amber-600' : 'text-teal-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-800">{t.trade}</p>
                        <p className="text-[10px] text-slate-500">{t.company}</p>
                      </div>
                      <div className="text-right space-y-0.5">
                        <p className="text-[10px] text-slate-600 font-medium">{t.crewCount || 0} crew</p>
                        <p className="text-[10px] text-slate-400">Since {t.checkInTime || '--:--'}</p>
                      </div>
                      <Badge className={`text-[9px] ${t.status === 'cannot-start' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-teal-50 text-teal-700 border-teal-200'}`}>
                        {t.status === 'cannot-start' ? 'Paused' : 'Working'}
                      </Badge>
                    </div>
                  ))}
                </div>
                {hideInternal && (
                  <p className="text-[10px] text-slate-400 mt-2 italic flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Worker contact details and internal notes hidden from client view</p>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Publish controls */}
        <Card className="border-slate-200">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-heading font-semibold text-base text-slate-900">Publish Controls</h3>
                <p className="text-xs text-slate-400">
                  Last published: {published ? new Date().toLocaleDateString('en-AU') : data.lastPublished}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={hideInternal} onCheckedChange={setHideInternal} id="hide-internal" data-testid="hide-internal-toggle" />
                  <label htmlFor="hide-internal" className="text-xs text-slate-600 cursor-pointer flex items-center gap-1">
                    <EyeOff className="w-3 h-3" /> Hide internal notes
                  </label>
                </div>
                <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 gap-1.5"
                  onClick={handlePublish} data-testid="publish-update-btn">
                  <Send className="w-3.5 h-3.5" />
                  {published ? "Update published" : "Publish update"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Weekly Update Composer */}
        <Card className="border-blue-100 bg-blue-50/20" data-testid="ai-weekly-composer">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                AI-drafted weekly update
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-[9px]">Live data</Badge>
                <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setEditDraft(!editDraft)} data-testid="edit-draft-btn">
                  <Edit className="w-3 h-3" /> {editDraft ? "Preview" : "Edit"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {editDraft ? (
              <textarea
                className="w-full bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700 leading-relaxed min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200"
                defaultValue={aiWeeklyDraft}
                data-testid="draft-textarea"
              />
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-700 leading-relaxed">{aiWeeklyDraft}</p>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <p className="text-[11px] text-blue-600">This draft is generated from live project state — cleared conditions, blocker count, and stage readiness. Human reviews before publish.</p>
            </div>
          </CardContent>
        </Card>

        {/* Client view preview */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-600">Preview of what the client sees</span>
          </div>

          <Card className="border-slate-300 shadow-md" data-testid="client-preview">
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: workspace.accentColor || '#2563eb' }}>
                  <Globe className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-heading font-semibold text-white">{data.project}</p>
                  <p className="text-xs text-slate-400">{data.address} · {workspace.name}</p>
                </div>
              </div>
              <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30 text-[10px]">Client Portal</Badge>
            </div>

            <CardContent className="py-6 px-6 space-y-6">
              {/* Progress bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-heading font-semibold text-base text-slate-900">Project Progress</h4>
                  <span className={`text-sm font-bold ${liveStats.overallProgress === 100 ? 'text-emerald-600' : 'text-blue-600'}`}>{liveStats.overallProgress}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${liveStats.overallProgress === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                    style={{ width: `${liveStats.overallProgress}%` }} />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mt-3">{data.progressSummary}</p>
              </div>

              {/* Milestone progress */}
              <div>
                <h4 className="font-heading font-semibold text-sm text-slate-900 mb-3">Milestone Progress</h4>
                <div className="flex items-center gap-0">
                  {data.milestoneProgress.map((m, i) => (
                    <div key={i} className="flex items-center flex-1">
                      <div className="flex-1 text-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1 ${
                          m.status === "complete" ? "bg-emerald-100" : m.status === "in-progress" ? "bg-blue-100" : "bg-slate-100"
                        }`}>
                          {m.status === "complete" ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          ) : m.status === "in-progress" ? (
                            <Clock className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Lock className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                        <p className="text-[11px] font-medium text-slate-700">{m.name}</p>
                        {m.date && <p className="text-[10px] text-slate-400">{m.date}</p>}
                      </div>
                      {i < data.milestoneProgress.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* This week - from live state */}
              <div>
                <h4 className="font-heading font-semibold text-sm text-slate-900 mb-2">Progress this week</h4>
                <ul className="space-y-1.5">
                  {data.weekHighlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Before / After placeholder */}
              <div>
                <h4 className="font-heading font-semibold text-sm text-slate-900 mb-3">Progress photos</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-100 rounded-lg h-32 flex flex-col items-center justify-center border border-slate-200 relative">
                    <Image className="w-8 h-8 text-slate-300 mb-1" />
                    <p className="text-[10px] text-slate-400 font-medium">Before — Site cleared</p>
                    <div className="absolute top-1.5 left-1.5"><Badge className="bg-slate-700/70 text-white text-[8px] border-0">Before</Badge></div>
                  </div>
                  <div className="bg-slate-100 rounded-lg h-32 flex flex-col items-center justify-center border border-slate-200 relative">
                    <Image className="w-8 h-8 text-slate-300 mb-1" />
                    <p className="text-[10px] text-slate-400 font-medium">After — Footings complete</p>
                    <div className="absolute top-1.5 left-1.5"><Badge className="bg-blue-600 text-white text-[8px] border-0">After</Badge></div>
                  </div>
                </div>
              </div>

              {/* Next milestone */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Next Milestone</p>
                    <p className="text-base font-heading font-semibold text-slate-900">{data.nextMilestone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Estimated</p>
                    <p className="text-sm font-medium text-slate-700">{data.nextMilestoneEta}</p>
                  </div>
                </div>
              </div>

              {/* Softened blockers */}
              <div>
                <h4 className="font-heading font-semibold text-sm text-slate-900 mb-2">Items in progress</h4>
                <ul className="space-y-1.5">
                  {data.blockersSoftened.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Clock className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              <Separator />

              {/* Shared documents */}
              <div>
                <h4 className="font-heading font-semibold text-sm text-slate-900 mb-2">Shared Documents</h4>
                <div className="space-y-1.5">
                  {data.sharedDocuments.map((doc, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5 hover:bg-slate-100 transition-colors cursor-pointer">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700">{doc.name}</p>
                        <p className="text-[10px] text-slate-400">{doc.date}</p>
                      </div>
                      <Download className="w-3.5 h-3.5 text-slate-300" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>

            <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 rounded-b-lg">
              <p className="text-[10px] text-slate-400 text-center">
                This is a curated project summary provided by {workspace.name}. For detailed enquiries, contact your project manager directly.
              </p>
            </div>
          </Card>
        </div>

        {!hideInternal && (
          <Card className="border-amber-200 bg-amber-50/30" data-testid="internal-notes-preview">
            <CardContent className="py-3 px-4 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-amber-800 font-medium">Internal notes visible</p>
                <p className="text-xs text-amber-600">These would show in the preview when "Hide internal notes" is off. Toggle it back on before publishing.</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
